import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, Divider, Grid, Stack, TextField, Typography } from '@mui/material';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FirebaseContext } from 'common';
import PublicLayout from '../components/PublicLayout.jsx';
import { resolveShopContext } from '../common/shopContext';
import { getSiteTheme, normalizeTemplateKey } from '../common/websiteTemplates';
import { formatMoney } from '../common/shopPricing';
import { isPartnerComingSoon, isPartnerMaintenance } from '../common/partnerUtils';
import PartnerUpsellPage from './PartnerUpsellPage';
import PartnerMaintenancePage from './PartnerMaintenancePage';
import PartnerComingSoonPage from './PartnerComingSoonPage';
import { getLocalizedShopItem } from '../common/shopContent';

const parseMs = (value) => {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
};

const formatDateTime = (value) => {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(
      new Date(value)
    );
  } catch (e) {
    return String(value);
  }
};

const formatCountdown = (ms) => {
  const safe = Math.max(0, Number(ms || 0));
  const s = Math.floor(safe / 1000);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
};

const maskUser = (userId) => {
  if (!userId) return '';
  const str = String(userId);
  if (str.length <= 8) return str;
  return str.slice(0, 6) + '...' + str.slice(-2);
};

export default function AuctionDetail() {
  const { partnerSlug, auctionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const firebase = useContext(FirebaseContext);
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);
  const { t, i18n } = useTranslation();

  const shopContext = useMemo(
    () =>
      resolveShopContext(settings, {
        partnerSlug,
        locale: i18n.language,
        pathname: location.pathname,
      }),
    [settings, partnerSlug, i18n.language, location.pathname]
  );

  const { partner, partnerKey, paths, websiteContent } = shopContext;
  const isAdmin = auth?.profile?.usertype === 'admin' || auth?.profile?.usertype === 'fleetadmin';
  const gateInactive = Boolean(partner?.inactive);
  const gateComingSoon = Boolean(partner && isPartnerComingSoon(partner) && !isAdmin);
  const gateMaintenance = Boolean(partner && isPartnerMaintenance(partner) && !isAdmin);

  const templateKey = normalizeTemplateKey(websiteContent.template || 'classic');
  const theme = getSiteTheme(templateKey, websiteContent.appearance || {});

  const shop = websiteContent.shop || {};
  const currency = shop.currency || 'USD';
  const shopEnabled = shop.enabled !== false;
  const uid = auth?.profile?.uid;

  const auction = useMemo(() => {
    const list = Array.isArray(shop.auctions) ? shop.auctions : [];
    const found = list.find((a) => a && (a.id === auctionId || a.slug === auctionId));
    return getLocalizedShopItem(found, i18n.language);
  }, [auctionId, shop.auctions, i18n.language]);

  const isPublished = auction?.published !== false;
  const startingBid = Number(auction?.startingBid || 0);
  const bidIncrement = Math.max(1, Number(auction?.bidIncrement || 1));
  const startMs = parseMs(auction?.startsAt);
  const endMs = parseMs(auction?.endsAt);

  const [nowMs, setNowMs] = useState(Date.now());
  useEffect(() => {
    if (gateInactive || gateComingSoon || gateMaintenance) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [gateInactive, gateComingSoon, gateMaintenance]);

  const status = useMemo(() => {
    if (!auction) return 'missing';
    if (!isPublished) return 'unpublished';
    if (startMs && nowMs < startMs) return 'upcoming';
    if (endMs && nowMs > endMs) return 'ended';
    return 'live';
  }, [auction, endMs, isPublished, nowMs, startMs]);

  const [auctionState, setAuctionState] = useState(null);
  const [bids, setBids] = useState([]);
  const [bidAmount, setBidAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (gateInactive || gateComingSoon || gateMaintenance) {
      setAuctionState(null);
      return;
    }
    if (!firebase?.database || !auction?.id) return;
    const ref = firebase.database.ref(`shop_auction_state/${partnerKey || 'global'}/${auction.id}`);
    ref.on('value', (snap) => setAuctionState(snap.val() || null));
    return () => ref.off();
  }, [firebase?.database, partnerKey, auction?.id, gateInactive, gateComingSoon, gateMaintenance]);

  useEffect(() => {
    if (gateInactive || gateComingSoon || gateMaintenance) {
      setBids([]);
      return;
    }
    if (!firebase?.database || !auction?.id) return;
    const ref = firebase.database.ref(`shop_auction_bids/${partnerKey || 'global'}/${auction.id}`).limitToLast(12);
    ref.on('value', (snap) => {
      const value = snap.val();
      if (!value) return setBids([]);
      const list = Object.entries(value).map(([id, row]) => ({ id, ...(row || {}) }));
      list.sort((a, b) => Date.parse(String(b.createdAt || '')) - Date.parse(String(a.createdAt || '')));
      setBids(list);
    });
    return () => ref.off();
  }, [firebase?.database, partnerKey, auction?.id, gateInactive, gateComingSoon, gateMaintenance]);

  const highestBid = Number(auctionState?.highestBid || 0);
  const bidCount = Number(auctionState?.bidCount || 0);
  const currentBid = highestBid > 0 ? highestBid : startingBid;
  const minNextBid = highestBid > 0 ? highestBid + bidIncrement : startingBid;

  const countdownLabel = useMemo(() => {
    if (status === 'upcoming' && startMs) return formatCountdown(startMs - nowMs);
    if (status === 'live' && endMs) return formatCountdown(endMs - nowMs);
    return '';
  }, [endMs, nowMs, startMs, status]);

  if (gateInactive) {
    return <PartnerUpsellPage partner={partner} />;
  }

  if (gateComingSoon) {
    return <PartnerComingSoonPage partner={partner} />;
  }

  if (gateMaintenance) {
    return <PartnerMaintenancePage partner={partner} />;
  }

  const canBid = shopEnabled && status === 'live';

  const placeBid = async () => {
    setError('');
    setSuccess('');

    if (!auction?.id) return;
    if (!uid) {
      navigate('/login', { state: { from: location.pathname || `${paths.auctions}/${encodeURIComponent(auctionId || '')}` } });
      return;
    }
    if (!firebase?.database?.ref) {
      setError(t('checkout_unavailable') || 'This feature is unavailable right now.');
      return;
    }
    if (!canBid) {
      setError(t('auction_not_live') || 'This auction is not live.');
      return;
    }

    const amount = Number(bidAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError(t('enter_valid_bid') || 'Enter a valid bid amount.');
      return;
    }
    if (amount < minNextBid) {
      setError((t('min_bid') || 'Minimum bid') + ': ' + formatMoney(minNextBid, currency));
      return;
    }

    setBusy(true);
    try {
      const stateRef = firebase.database.ref(`shop_auction_state/${partnerKey || 'global'}/${auction.id}`);
      const result = await stateRef.transaction(
        (current) => {
          const nowIso = new Date().toISOString();
          const curHighest = Number(current?.highestBid || 0);
          const curCount = Number(current?.bidCount || 0);
          const requiredMin = curHighest > 0 ? curHighest + bidIncrement : startingBid;
          if (amount < requiredMin) return;
          return {
            ...(current || {}),
            highestBid: amount,
            highestBidder: uid,
            bidCount: curCount + 1,
            lastBidAt: nowIso,
          };
        },
        undefined,
        false
      );

      if (!result?.committed) {
        setError(t('bid_too_low') || 'Bid too low (or someone outbid you). Please try again.');
        return;
      }

      await firebase.database.ref(`shop_auction_bids/${partnerKey || 'global'}/${auction.id}`).push({
        amount,
        userId: uid,
        userName: auth?.profile?.firstName || auth?.profile?.name || '',
        createdAt: new Date().toISOString(),
      });

      setSuccess(t('bid_placed') || 'Bid placed!');
      setBidAmount('');
    } catch (e) {
      console.error('Failed to place bid:', e);
      setError(t('bid_failed') || 'Failed to place bid. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (!shopEnabled) {
    return (
      <PublicLayout title={t('auctions') || 'Auctions'} partner={partner} homePath={paths.home} bookingPath={paths.booking}>
        <Box sx={{ maxWidth: 1000, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 6, md: 8 } }}>
          <Alert severity="info">{t('shop_disabled_help') || 'Please check back later.'}</Alert>
          <Button
            variant="contained"
            onClick={() => navigate(paths.shop)}
            sx={{ mt: 2, borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
          >
            {t('back_to_shop') || 'Back to shop'}
          </Button>
        </Box>
      </PublicLayout>
    );
  }

  if (!auction) {
    return (
      <PublicLayout title={t('auctions') || 'Auctions'} partner={partner} homePath={paths.home} bookingPath={paths.booking}>
        <Box sx={{ maxWidth: 1000, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 6, md: 8 } }}>
          <Alert severity="warning">{t('auction_not_found') || 'Auction not found.'}</Alert>
          <Button
            variant="contained"
            onClick={() => navigate(paths.auctions)}
            sx={{ mt: 2, borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
          >
            {t('back') || 'Back'}
          </Button>
        </Box>
      </PublicLayout>
    );
  }

  const statusChip =
    status === 'live' ? (
      <Chip label={t('live') || 'Live'} color="success" size="small" sx={{ fontWeight: 900 }} />
    ) : status === 'upcoming' ? (
      <Chip label={t('upcoming') || 'Upcoming'} size="small" sx={{ fontWeight: 900 }} />
    ) : status === 'ended' ? (
      <Chip label={t('ended') || 'Ended'} size="small" sx={{ fontWeight: 900 }} />
    ) : (
      <Chip label={t('unpublished') || 'Unpublished'} size="small" sx={{ fontWeight: 900 }} />
    );

  return (
    <PublicLayout
      title={auction.title}
      description={auction.description}
      partner={partner}
      homePath={paths.home}
      bookingPath={paths.booking}
    >
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 6, md: 8 } }}>
        <Button
          variant="outlined"
          onClick={() => navigate(paths.auctions)}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900, mb: 3 }}
        >
          {t('back') || 'Back'}
        </Button>

        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                borderRadius: 4,
                overflow: 'hidden',
                border: `1px solid ${theme.border}`,
                bgcolor: theme.sectionAltBg,
                height: { xs: 300, md: 440 },
                position: 'relative',
              }}
            >
              {auction.image ? (
                <Box component="img" src={auction.image} alt={auction.title} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'radial-gradient(900px 360px at 25% 30%, rgba(250,204,21,0.25), transparent 60%), radial-gradient(900px 360px at 85% 30%, rgba(34,197,94,0.20), transparent 60%), linear-gradient(135deg, #0b1120 0%, #111827 60%)',
                  }}
                />
              )}
              <Box sx={{ position: 'absolute', top: 14, left: 14 }}>{statusChip}</Box>
              {countdownLabel ? (
                <Chip
                  label={(status === 'upcoming' ? (t('starts_in') || 'Starts in') : (t('ends_in') || 'Ends in')) + ` ${countdownLabel}`}
                  size="small"
                  sx={{ position: 'absolute', top: 14, right: 14, fontWeight: 900, bgcolor: 'rgba(0,0,0,0.55)', color: '#fff' }}
                />
              ) : null}
              <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.12)' }} />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              <Box>
                <Typography sx={{ fontWeight: 950, color: theme.text, fontSize: { xs: 30, md: 40 }, letterSpacing: -1 }}>
                  {auction.title}
                </Typography>
                {auction.description ? (
                  <Typography sx={{ color: theme.muted, mt: 1, lineHeight: 1.7 }}>
                    {auction.description}
                  </Typography>
                ) : null}
              </Box>

              {error ? <Alert severity="error">{error}</Alert> : null}
              {success ? <Alert severity="success">{success}</Alert> : null}

              <Card variant="outlined" sx={{ borderRadius: 3, borderColor: theme.border, bgcolor: theme.cardBg }}>
                <CardContent>
                  <Stack spacing={1.25}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography sx={{ color: theme.muted }}>{t('current_bid') || 'Current bid'}</Typography>
                      <Typography sx={{ fontWeight: 950, color: theme.text, fontSize: 18 }}>
                        {formatMoney(currentBid, currency)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography sx={{ color: theme.muted }}>{t('bids') || 'Bids'}</Typography>
                      <Typography sx={{ fontWeight: 900, color: theme.text }}>{bidCount}</Typography>
                    </Stack>
                    <Divider />
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography sx={{ color: theme.muted }}>{t('min_bid') || 'Minimum bid'}</Typography>
                      <Typography sx={{ fontWeight: 950, color: theme.text }}>{formatMoney(minNextBid, currency)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography sx={{ color: theme.muted }}>{t('increment') || 'Increment'}</Typography>
                      <Typography sx={{ fontWeight: 900, color: theme.text }}>{formatMoney(bidIncrement, currency)}</Typography>
                    </Stack>
                    {auction.startsAt ? (
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ color: theme.muted }}>{t('starts') || 'Starts'}</Typography>
                        <Typography sx={{ fontWeight: 900, color: theme.text }}>{formatDateTime(auction.startsAt)}</Typography>
                      </Stack>
                    ) : null}
                    {auction.endsAt ? (
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ color: theme.muted }}>{t('ends') || 'Ends'}</Typography>
                        <Typography sx={{ fontWeight: 900, color: theme.text }}>{formatDateTime(auction.endsAt)}</Typography>
                      </Stack>
                    ) : null}
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ borderRadius: 3, borderColor: theme.border, bgcolor: theme.sectionAltBg }}>
                <CardContent>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <GavelRoundedIcon fontSize="small" />
                      <Typography sx={{ fontWeight: 950, color: theme.text }}>
                        {t('place_bid') || 'Place a bid'}
                      </Typography>
                    </Stack>
                    <TextField
                      type="number"
                      value={bidAmount}
                      onChange={(e) => setBidAmount(e.target.value)}
                      label={t('your_bid') || 'Your bid'}
                      inputProps={{ min: minNextBid, step: bidIncrement }}
                      helperText={(t('min_bid') || 'Minimum bid') + ': ' + formatMoney(minNextBid, currency)}
                      fullWidth
                      disabled={!canBid || busy}
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} justifyContent="flex-end">
                      {!uid ? (
                        <Button
                          variant="outlined"
                          onClick={() => navigate('/login', { state: { from: location.pathname } })}
                          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
                        >
                          {t('login') || 'Log in'}
                        </Button>
                      ) : null}
                      <Button
                        variant="contained"
                        onClick={placeBid}
                        disabled={!canBid || busy}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 900,
                          bgcolor: theme.accent || '#111827',
                          '&:hover': { bgcolor: theme.accent || '#0b1224' },
                        }}
                      >
                        {busy ? (t('placing_bid') || 'Placing bid...') : (t('place_bid') || 'Place bid')}
                      </Button>
                    </Stack>
                    {!canBid ? (
                      <Typography variant="body2" sx={{ color: theme.muted }}>
                        {status === 'upcoming'
                          ? (t('auction_upcoming') || 'This auction has not started yet.')
                          : status === 'ended'
                            ? (t('auction_ended') || 'This auction has ended.')
                            : (t('auction_unpublished') || 'This auction is not published.')}
                      </Typography>
                    ) : null}
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4 }}>
          <Typography sx={{ fontWeight: 950, color: theme.text, fontSize: 18, mb: 1.5 }}>
            {t('recent_bids') || 'Recent bids'}
          </Typography>
          {!bids.length ? (
            <Typography sx={{ color: theme.muted }}>
              {t('no_bids_yet') || 'No bids yet. Be the first.'}
            </Typography>
          ) : (
            <Card variant="outlined" sx={{ borderRadius: 3, borderColor: theme.border, bgcolor: theme.cardBg }}>
              <CardContent>
                <Stack spacing={1}>
                  {bids.map((bid) => (
                    <Stack key={bid.id} direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" sx={{ color: theme.muted }}>
                        {bid.userName ? bid.userName : maskUser(bid.userId)}
                        {bid.createdAt ? ` · ${formatDateTime(bid.createdAt)}` : ''}
                      </Typography>
                      <Typography sx={{ fontWeight: 950, color: theme.text }}>
                        {formatMoney(bid.amount, currency)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>
    </PublicLayout>
  );
}
